import { injectable } from 'inversify'
import { Vector2 } from 'three'
import type { IPoint2 } from '@/plugins/cad/module/common/interface'

/**
 * 正交吸附参数。
 */
export interface IOrthogonalOption {
  /** 正交参考起点，缺失时跳过正交计算 */
  startPoint?: IPoint2
}

/**
 * 正交吸附辅助器，将当前点按与起点的主轴距离强制对齐到水平或垂直方向。
 */
@injectable()
export class OrthogonalHelper {
  // 是否开启正交
  private enabled: boolean = false

  /**
   * 开关正交吸附。
   *
   * @param val - 是否开启
   */
  setEnabled(val: boolean) {
    this.enabled = val
  }

  /**
   * 查询当前正交开关状态。
   *
   * @returns 是否开启
   */
  isEnabled() {
    return this.enabled
  }

  /**
   * 计算正交吸附后的点。
   *
   * @param worldPos - 当前世界坐标点
   * @param option - 正交吸附参数
   * @returns 吸附后的点；未开启或缺少起点时返回 undefined
   */
  getOrthogonalInfo(worldPos: IPoint2, option: IOrthogonalOption | undefined) {
    // 不进行正交：未开启正交、无startPoint、
    if (!this.enabled || !option?.startPoint) {
      return
    }

    const startPoint = new Vector2(option.startPoint.x, option.startPoint.y)
    const currentPoint = new Vector2(worldPos.x, worldPos.y)
    const diff = currentPoint.clone().sub(startPoint)

    // 计算x轴、y轴的距离
    const distX = Math.abs(diff.x)
    const distY = Math.abs(diff.y)

    if (distX > distY) {
      return {
        x: currentPoint.x,
        y: startPoint.y,
      }
    } else {
      return {
        x: startPoint.x,
        y: currentPoint.y,
      }
    }
  }
}
